<template>
    <div>
        <div class="pb-5 pt-8 px-6">
            <div class="flex items-center">
                <button @click="closeTray" class="bg-v2-white border border-v2-gray-110 flex gap-1 items-center font-medium mr-auto pl-1 pr-3 py-0.5 rounded text-v2-gray-40 text-xs hover:bg-v2-gray-130 dark:bg-v2-gray-60 dark:border-v2-gray-10 dark:text-v2-gray-90 dark:hover:bg-v2-gray-70">
                    <BaseSvg name="ChevronLeft" aria-hidden="true" class="fill-v2-gray-70 size-5 dark:fill-v2-gray-90" />
                    All Pins
                </button>
                <button @click="forceClose" class="bg-v2-white border border-v2-gray-110 flex rounded hover:bg-v2-gray-130 dark:bg-v2-gray-20 dark:border-v2-gray-10 dark:hover:bg-v2-gray-70">
                    <BaseSvg name="Close" aria-hidden="true" class="fill-v2-gray-70 size-[18px] dark:fill-v2-gray-90" />
                    <span class="sr-only">Close</span>
                </button>
            </div>
            <h2 class="font-semibold leading-tight mt-5 text-base text-v2-gray-20 dark:text-v2-white">
                {{ drawer.selected_tray.name }}
                <span class="font-medium text-v2-gray-80 text-sm dark:text-v2-gray-90">({{drawer.selected_tray?.pin_count ?? 0}})</span>
            </h2>
            <!-- Optional tray description. -->
            <div v-if="drawer.selected_tray.name !== 'Favorites'">
                <p
                    @click="editDescription"
                    v-if="!isEditingDescription"
                    :class="drawer.selected_tray.description.length ? '' : 'italic'"
                    class="group relative flex items-center duration-150 rounded px-1 py-px hover:bg-gray-tier-6 hover:type-primary w-fit -ml-1 leading-snug mt-1 text-v2-gray-80 text-xs"
                >
                    {{ drawer.selected_tray.description || "Add a short description" }}
                    <ButtonTooltip class="hidden group-hover:block not-italic" text="Update"  />
                </p>
                <div v-click-outside="cancel" v-else class="relative">
                    <input
                        ref="newDescriptionRef"
                        type="text"
                        @keyup.enter="updateDescription"
                        @keyup.stop.esc="cancel"
                        v-model="newDescriptionInput"
                        class="w-full flex mt-1 -ml-1 px-1 py-px rounded text-xs border-none outline-none focus-within:ring-0 bg-gray-tier-6"
                    />
                    <div class="bg-white p-2 rounded-lg shadow top-full mt-1 absolute flex items-center gap-2">
                        <AppButton @click="cancel" size="small" icon="close" text="Cancel" />
                        <AppButton @click="updateDescription" theme="green" size="small" icon="check" text="Save" />
                    </div>
                </div>
            </div>
        </div>
        <div class="border-t border-v2-gray-110 flex flex-col gap-2 py-5 px-6 at1440:flex-row dark:border-v2-gray-10">
            <SearchInput
                v-model="computedSearchInput"
                placeholder="Search this tray"
                class="grow bg-v2-gray-130 border-v2-gray-110 dark:bg-v2-gray-30 dark:border-v2-gray-10  !h-[34px]"
                :large="true"
                :disabled="!drawer.pins.length"
            />
            <SeclockMultiselect
                :disabled="!searchHasResults || !drawer.pins.length"
                v-model="computedSortValue"
                :options="searchSortOptions"
                :can-deselect="false"
                :option-is-object="true"
                :label="$t('Sort')"
                class="flex !w-full"
                label-position="side"
                :contrast="true"
                :hideSelected="true"
            />
        </div>
        <Divider />
    </div>
</template>

<script setup>
import {AppButton, BaseSvg, ButtonTooltip, SearchInput, SeclockMultiselect, Divider} from "@/Components";
import {useDialog, usePinsDrawer, usePinSearch} from "@/Composables";
import {computed, nextTick, ref} from "vue";

const { drawer, closeTray, updateTray } = usePinsDrawer();
const { forceClose } = useDialog()

const { searchHasResults, searchSortOptions } = usePinSearch();

const props = defineProps(['modelValue', 'sortValue']);
const emit = defineEmits(["update:modelValue", 'update:sortValue']);

const computedSearchInput = computed({
    get: () => props.modelValue,
    set: (value) => emit("update:modelValue", value),
});

const computedSortValue = computed({
    get: () => props.sortValue,
    set: (value) => emit("update:sortValue", value),
})

// Description Editting
const newDescriptionRef = ref();
const newDescriptionInput = ref("");
const isEditingDescription = ref(false);
function editDescription () {
    newDescriptionInput.value = drawer.value?.selected_tray?.description;
    isEditingDescription.value = true;
    nextTick(() => {
        newDescriptionRef.value?.focus();
    });
}

function updateDescription () {
    updateTray(drawer.value.selected_tray, drawer.value.selected_tray.name, newDescriptionInput.value, drawer.value.selected_tray?.icon);
    drawer.value.selected_tray.description = newDescriptionInput.value;
    isEditingDescription.value = false;
}

function cancel () {
    newDescriptionInput.value = "";
    isEditingDescription.value = false;
}
</script>
